import { EnrollmentContextService } from '../components/hoc/context/enrollment/enrollment-context';
import { ConfigurationModel } from '../components/hoc/context/product/configuration-model';
import { Question } from '../hooks/useQuestions';
import {
	EventAction,
	EventArgs,
	EventKeys,
	Identifiers,
	EventPosition,
} from './analytics-interfaces';

export const ageFieldKeyValue = (
	performer: string | undefined,
	choice: string | undefined
): { [x: string]: string } => {
	const ageField =
		performer === Identifiers.CareGiver
			? EventKeys.PatientAge
			: EventKeys.OwnAge;
	return {
		[ageField]: choice !== 'false' ? '18 years or older' : 'Under age 18',
	};
};

export const getCustomKeyValues = (
	choice: string | undefined,
	actualChoice: string | undefined,
	question: Question,
	productConfig: ConfigurationModel,
	enrollmentCtxt: EnrollmentContextService
): EventArgs => {
	choice = choice ?? '';
	const { id } = question;
	switch (id.toLowerCase()) {
		case Identifiers.Performer:
			const labelMapper = {
				patient: 'Myself',
				caregiver: 'Someone I care for',
			};
			return {
				[EventKeys.EventAction]: EventAction.EnrollPerson,
				[EventKeys.EnrollPerson]:
					labelMapper[choice as 'patient' | 'caregiver'],
			};
		case Identifiers.BirthDate:
			return {
				[EventKeys.EventAction]: EventAction.Age,
				...ageFieldKeyValue(enrollmentCtxt.performer, choice),
			};
		case Identifiers.Consent:
			return {
				[EventKeys.EventAction]: EventAction.Consent,
				[EventKeys.CaregiverConsent]: 'Yes',
			};
		case Identifiers.PrimaryCondition:
			return {
				[EventKeys.EventAction]: EventAction.PrimaryCondition,
				[EventKeys.PrimaryCondition]: choice,
			};
		case Identifiers.Insurance:
			return {
				[EventKeys.EventAction]: EventAction.PrescriptionInsurance,
				[EventKeys.PrescriptionInsurance]: choice,
			};
		case Identifiers.PatientEligibility:
			const eligibleServices =
				actualChoice ??
				enrollmentCtxt
					.eligibleServices(productConfig.services)
					.flatMap((service) => service.name)
					.join(',');
			return {
				[EventKeys.EventAction]: EventAction.PatientEligibility,
				[EventKeys.PatientEligibility]: eligibleServices,
			};
		case Identifiers.FTO:
			return {
				[EventKeys.EventAction]: EventAction.PatientEligibility,
				[EventKeys.PatientEligibility]: Identifiers.FTO,
			};
		case Identifiers.Region:
			return Object.assign({
				[EventKeys.EventAction]: EventAction.Region,
			});
		default:
			return { [EventKeys.EventAction]: choice };
	}
};

export const replaceHtmlTags = (strWithHtmlTags: string, strTarget = ' ') => {
	try {
		let resStr = strWithHtmlTags.replace(/<[^>]*>/g, strTarget);
		if (resStr) {
			resStr = resStr.trim();
		}
		return resStr;
	} catch (err) {
		return strWithHtmlTags;
	}
};

type EventPositionKeys = keyof typeof EventPosition;
const isEventKey = (
	position: string | undefined
): position is EventPositionKeys => {
	if (!position) {
		return false;
	}
	return Object.keys(EventPosition).includes(position);
};

export function getPosition(position: string | undefined) {
	if (!isEventKey(position)) {
		return '';
	}
	return EventPosition[position];
}
